import ApproveRequiredTable from "./ApproveRequired";
import { type GridRowClickEvent } from "@progress/kendo-react-grid";

import DialogXl from "@/app/models/dialogs/DialogXl";
import { useT } from "@/app/hooks/useT";
import { Loader } from "@progress/kendo-react-indicators";
import NoDataFound from "@/app/components/common/warning/NoDataFound";
import SearchInput from "@/app/components/common/actions/inputs-b/SearchInput";
import {
  memo,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useDebounce } from "@/app/hooks/useDebounce";
import type { ApprovalRequiredTransactions } from "@/app/types/payments";
import GxHorizontalLine from "@/app/components/common/actions/buttons/GxHorizontalLine";
import GxRedRejectButton from "@/app/components/common/actions/buttons/GxRedRejectButton";
import GxPrimaryButton from "@/app/components/common/actions/buttons/GxPrimaryButton";
import { gxRTPPaymentApprovalListApi } from "@/app/apiConfig/services/paymentsAbstractApiLayer";

interface RtpPaymentsApprovalProps {
  openRtpInitiatedPayment: boolean;
  handleRtpInitiatedPaymentDrawer: () => void;
  handleRowClick: (value: GridRowClickEvent) => void;
  checkAll: boolean;
  selectedTransactionList: number[];
  setSelectedTransactionList: Dispatch<SetStateAction<number[]>>;
  setCheckAll: Dispatch<SetStateAction<boolean>>;
  handleBulkReject: () => void;
  handleBulkApprove: () => void;
  callPayByBankList: boolean;
  setCallPayByBankList: Dispatch<SetStateAction<boolean>>;
}

const RtpPaymentsApproval = memo(
  ({
    openRtpInitiatedPayment,
    handleRtpInitiatedPaymentDrawer,
    handleRowClick,
    checkAll,
    selectedTransactionList,
    setSelectedTransactionList,
    setCheckAll,
    handleBulkReject,
    handleBulkApprove,
    callPayByBankList,
    setCallPayByBankList,
  }: Readonly<RtpPaymentsApprovalProps>) => {
    const t = useT();
    const [approvalList, setApprovalList] = useState<
      ApprovalRequiredTransactions[]
    >([]);
    const [searchApproval, setSearchApproval] = useState<string>();
    const debouncedInput = useDebounce(searchApproval || "", 500);
    const [page, setPage] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(false);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const loader = useRef<HTMLDivElement | null>(null);

    const getApproveList = async (makeEmpty: boolean) => {
      try {
        const payload = {
          pageNo: page,
          pageSize: 10,
          sortBy: "Asc",
          status: "4415",
          searchText: debouncedInput,
        };
        const result = await gxRTPPaymentApprovalListApi(payload);
        if (result?.data) {
          setApprovalList((prev) => {
            const newData = makeEmpty
              ? result?.data
              : [...prev, ...result.data];
            // remove duplicates by transId
            const unique = new Map(
              newData?.map((item) => [item.transId, item]),
            );
            return Array.from(unique.values());
          });
          if (result.data.length < 10) {
            setHasMore(false);
          }
        } else {
          setHasMore(false);
        }
      } catch (err) {
        console.log("Error in getApproveList::", err);
        setHasMore(false);
      } finally {
        setLoading(false);
        setCallPayByBankList(false);
      }
    };

    useEffect(() => {
      setPage(1);
      setHasMore(true);
      getApproveList(true);
    }, [debouncedInput]);

    useEffect(() => {
      if (page > 1 && hasMore) {
        getApproveList(false);
      }
    }, [page]);

    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !loading && hasMore) {
            if (approvalList.length === 10) {
              setPage((prev) => prev + 1);
            }
          }
        },
        { threshold: 1 },
      );

      if (loader.current) {
        observer.observe(loader.current);
      }

      return () => {
        if (loader.current) {
          observer.unobserve(loader.current);
        }
      };
    }, [loading, hasMore]);

    useEffect(() => {
      if (callPayByBankList) {
        setPage(1);
        setHasMore(true);
        getApproveList(true);
      }
    }, [callPayByBankList]);

    return (
      <DialogXl
        title={t("approval")}
        subtitle={t("required")}
        open={openRtpInitiatedPayment}
        handleClose={handleRtpInitiatedPaymentDrawer}
        showFooter={false}
      >
        <div className="gx-approve-required-modal">
          <div>
            <div>
              <div className="gx-approve-required-filter-search-container gx-mt-3">
                <div className="gx-approve-required-search-wrapper">
                  <SearchInput
                    placeholder={t("search_growx_ref_bene")}
                    searchQuery={searchApproval || ""}
                    onSearchChange={setSearchApproval}
                  />
                </div>
              </div>
            </div>
            <div className="gx-mt-3">
              {approvalList?.length > 0 ? (
                <div className="gx-approve-required-table-container">
                  <ApproveRequiredTable
                    tableList={approvalList}
                    onRowClick={handleRowClick}
                    selectedTransactionList={selectedTransactionList}
                    setSelectedTransactionList={setSelectedTransactionList}
                    showAll={true}
                    setCheckAll={setCheckAll}
                    checkAll={checkAll}
                  />
                </div>
              ) : (
                <div className="gx-approve-required-do-data">
                  <NoDataFound message="No Approval Found" />
                </div>
              )}
            </div>
            <div ref={loader} className="gx-approve-required-loader">
              {loading && <Loader size="medium" type="converging-spinner" />}
            </div>
          </div>
          <div className="gx-dialog-actions gx-footer-drawer-container">
            <GxHorizontalLine />
            <GxRedRejectButton onClick={handleBulkReject}>
              Reject
            </GxRedRejectButton>

            <GxPrimaryButton
              handleSubmit={handleBulkApprove}
              label={t("approve")}
            />
          </div>
        </div>
      </DialogXl>
    );
  },
);

export default RtpPaymentsApproval;
